import type { BotConfig } from '../../types/botConfig.js';

const API_BASE_URL = '';

export interface TokenData {
  name: string;
  symbol: string;
  price: number;
  liquidity: number | null;
  mcap: number | null;
  holders: number | null;
  risk: 'LOW' | 'MEDIUM' | 'HIGH';
  logo?: string;
}

export interface WorkerInfo {
  workerPublicKey: string;
  network: string;
  adminWallet: string | null;
  minPaymentSol: number;
  packageType: string;
}

export interface StartBotRequest {
  mintAddress: string;
  packageType: '0.5 SOL' | '4.5 SOL';
  targetVolume?: number;
  userPaidAmount?: number;
  poolAddress?: string;
  userWallet?: string;
  paymentSignature?: string;
  botConfig?: BotConfig;
  network?: 'devnet' | 'mainnet-beta';
}

export interface BotStatus {
  job: {
    job_id: string;
    user_wallet: string;
    token_mint: string;
    pool_address: string | null;
    package_type: string;
    target_volume: number | null;
    user_paid_amount: number | null;
    status: string;
    total_transactions: number;
    total_volume: number;
    created_at: number;
    started_at: number;
    completed_at: number | null;
    error: string | null;
    payment_signature?: string | null;
    config_json?: string | null;
  };
  transactions: Array<{
    tx_signature: string;
    job_id: string;
    transaction_type: string;
    amount: number | null;
    direction: 'buy' | 'sell' | null;
    timestamp: number;
  }>;
  statusUpdates: Array<{
    job_id: string;
    status: string;
    message: string;
    timestamp: number;
  }>;
}

class ApiService {
  async fetchTokenData(mintAddress: string): Promise<TokenData> {
    const response = await fetch(`${API_BASE_URL}/api/token/info?mint=${mintAddress}`);
    if (!response.ok) {
      throw new Error('Failed to fetch token data');
    }
    return response.json();
  }

  async getWorkerInfo(packageType: string, poolAddress?: string): Promise<WorkerInfo> {
    const params = new URLSearchParams({ packageType });
    if (poolAddress) params.append('poolAddress', poolAddress);
    const response = await fetch(`${API_BASE_URL}/api/volume-bot/worker?${params.toString()}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch worker info');
    }
    return response.json();
  }

  async startBot(request: StartBotRequest): Promise<{ status: string; jobId: string }> {
    const response = await fetch(`${API_BASE_URL}/api/volume-bot/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to start bot');
    }

    return response.json();
  }

  async stopBot(jobId: string): Promise<{ status: string; job: BotStatus['job'] }> {
    const response = await fetch(`${API_BASE_URL}/api/volume-bot/stop/${jobId}`, {
      method: 'POST',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to stop bot');
    }

    return response.json();
  }

  async getBotStatus(jobId: string): Promise<BotStatus> {
    const response = await fetch(`${API_BASE_URL}/api/volume-bot/status/${jobId}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch bot status');
    }
    return response.json();
  }

  async getBotJobs(userWallet?: string, status?: string) {
    const params = new URLSearchParams();
    if (userWallet) params.append('userWallet', userWallet);
    if (status) params.append('status', status);
    const response = await fetch(`${API_BASE_URL}/api/volume-bot/jobs?${params.toString()}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch bot jobs');
    }
    return response.json();
  }
}

export const apiService = new ApiService();
